import { useState, useEffect } from "react";
import { useContactModal } from "@/hooks/useContactModal";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Mail, Send, Loader2 } from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

console.log('ContactModal API_URL:', API_URL); // Debug log

export function ContactModal() {
  const { isOpen, toEmail, prefill, closeModal } = useContactModal();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    fromEmail: user?.email || '',
    subject: '',
    message: '',
  });

  // Pre-fill subject + message when opened from chatbot
  useEffect(() => {
    if (isOpen && prefill) {
      setFormData(prev => ({ ...prev, subject: prefill.subject, message: prefill.message }));
    }
    if (!isOpen) {
      setFormData({ fromEmail: user?.email || '', subject: '', message: '' });
    }
  }, [isOpen, prefill]);
  
  const [errors, setErrors] = useState({
    fromEmail: '',
    subject: '',
    message: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors = {
      fromEmail: '',
      subject: '',
      message: '',
    };

    if (!formData.fromEmail.trim()) {
      newErrors.fromEmail = 'Email is required';
    } else if (!validateEmail(formData.fromEmail)) {
      newErrors.fromEmail = 'Invalid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('[Contact] Sending contact email...');
      console.log('[Contact] API URL:', API_URL);
      console.log('[Contact] Request data:', {
        fromEmail: formData.fromEmail,
        toEmail: toEmail,
        subject: formData.subject,
      });

      const response = await fetch(`${API_URL}/api/contact/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fromEmail: formData.fromEmail,
          toEmail: toEmail,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      const data = await response.json();
      
      console.log('[Contact] Response received:', response.status, data);

      if (response.ok) {
        console.log('[Contact] Email sent successfully');
        toast({
          title: "Email Sent Successfully",
          description: "Your message has been sent. We'll get back to you soon!",
        });
        
        // Reset form
        setFormData({
          fromEmail: user?.email || '',
          subject: '',
          message: '',
        });
        closeModal();
      } else {
        console.error('[Contact] Server returned error:', data);
        throw new Error(data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('[Contact] Error sending email:', error);
      
      let errorMessage = "Please try again later.";
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = "Unable to connect to server. Please ensure the server is running on port 3030.";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Failed to Send Email",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        fromEmail: user?.email || '',
        subject: '',
        message: '',
      });
      setErrors({
        fromEmail: '',
        subject: '',
        message: '',
      });
      closeModal();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Contact Us
          </DialogTitle>
          <DialogDescription>
            Send us a message and we'll get back to you as soon as possible.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="fromEmail">
              Your Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="fromEmail"
              type="email"
              placeholder="your.email@example.com"
              value={formData.fromEmail}
              onChange={(e) => handleChange('fromEmail', e.target.value)}
              disabled={isSubmitting}
              className={errors.fromEmail ? 'border-destructive' : ''}
            />
            {errors.fromEmail && (
              <p className="text-sm text-destructive">{errors.fromEmail}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="toEmail">To</Label>
            <Input
              id="toEmail"
              type="email"
              value={toEmail}
              disabled
              className="bg-muted"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">
              Subject <span className="text-destructive">*</span>
            </Label>
            <Input
              id="subject"
              type="text"
              placeholder="What is this regarding?"
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              disabled={isSubmitting}
              className={errors.subject ? 'border-destructive' : ''}
            />
            {errors.subject && (
              <p className="text-sm text-destructive">{errors.subject}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">
              Message <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="message"
              placeholder="Type your message here..."
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              disabled={isSubmitting}
              rows={6}
              className={errors.message ? 'border-destructive' : ''}
            />
            {errors.message && (
              <p className="text-sm text-destructive">{errors.message}</p>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="gap-2">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
